import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
  isIdXboxGameInformationConfigurationsFormValid,
  isIdXboxGameInformationDetailsFormValid,
  isIdXboxGameInformationGameExpectationsValid,
  isIdXboxGameInformationPricingAndAvailabilityFormValid,
  isIdXboxGameInformationSupplementaryDataFormValid,
  isIdXboxGameInformationTechnicalCapabilitiesFormValid,
  isIdXboxGameInformationUgcFormValid,
  isIdXboxGameInformationXboxNetworkFormValid,
  isIdXboxIntroContactFormValid,
  isIdXboxIntroStudioFormValid,
} from "../../helpers/formValidation";
import { compareIdXboxApplicationStepStatus } from "../../helpers/idXboxStatusHelpers";
import {
  GameInformationConceptDetailsDataType,
  GameInformationConfigurationsDataType,
  GameInformationGameExpectationsDataType,
  GameInformationPricingAndAvailabilityDataType,
  GameInformationSupplementaryDataType,
  GameInformationTechnicalCapabilitiesDataType,
  GameInformationUgcDataType,
  GameInformationXboxNetworkDataType,
  IdXboxIntroContactDataType,
  IdXboxIntroStudioDataType,
} from "../../models/forms";
import { IdXboxApplicationStep } from "../../models/status";
import {
  GameInformationFormId,
  GameInformationSubFormsId,
} from "../../pages/GameInformationPage/GameInformationPageConstants";
import {
  IdXboxIntroFormId,
  IdXboxIntroSubFormsId,
} from "../../pages/IdXboxIntroPage/IdXboxIntroPageConstants";
import { OnboardingRootState } from "../../redux/reducers/rootReducer";
import { getIdXboxApplicationStep, getSubFormData } from "../../redux/selectors";

const stepPercentageValue = 10;
const validFormsPercentage = 40;
const increasePercentageIfValid = (
  isFormValid: boolean,
  currentPercentage: number,
  multiplier: number = 1
): number =>
  isFormValid ? currentPercentage + stepPercentageValue * multiplier : currentPercentage;

export const useIdXboxGameConceptPercentage = () => {
  const [percentage, setPercentage] = useState(0);
  const currentStep = useSelector(getIdXboxApplicationStep) ?? IdXboxApplicationStep.Unset;

  // ID@Xbox Game Information sub form data
  const gameInfoConceptSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, GameInformationFormId, GameInformationSubFormsId.ConceptDetails)
  ) as GameInformationConceptDetailsDataType;
  const gameInfoGameExpectationsSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, GameInformationFormId, GameInformationSubFormsId.GameExpectations)
  ) as GameInformationGameExpectationsDataType;
  const gameInfoPricingAndAvailabilitiesSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, GameInformationFormId, GameInformationSubFormsId.PricingAndAvailability)
  ) as GameInformationPricingAndAvailabilityDataType;
  const gameInfoTechnicalCapabilitiesSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, GameInformationFormId, GameInformationSubFormsId.TechnicalCapabilities)
  ) as GameInformationTechnicalCapabilitiesDataType;
  const gameInfoConfigurationsSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, GameInformationFormId, GameInformationSubFormsId.Configurations)
  ) as GameInformationConfigurationsDataType;
  const gameInfoXboxNetworkSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, GameInformationFormId, GameInformationSubFormsId.XboxNetwork)
  ) as GameInformationXboxNetworkDataType;
  const gameInfoUgcSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, GameInformationFormId, GameInformationSubFormsId.Ugc)
  ) as GameInformationUgcDataType;
  const gameInfoSupplementaryDataSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, GameInformationFormId, GameInformationSubFormsId.SupplementaryData)
  ) as GameInformationSupplementaryDataType;

  useEffect(() => {
    const calculatePercentage = async () => {
      let tempPercentage = 0;

      // Game Information
      const isGameInfoConceptFormValid =
        (await isIdXboxGameInformationDetailsFormValid(gameInfoConceptSubFormData)) &&
        gameInfoConceptSubFormData.isStarted;
      const isGameInfoGameExpectationsFormValid =
        (await isIdXboxGameInformationGameExpectationsValid(gameInfoGameExpectationsSubFormData)) &&
        gameInfoGameExpectationsSubFormData.isStarted;
      const isGameInfoPricingFormValid =
        (await isIdXboxGameInformationPricingAndAvailabilityFormValid(
          gameInfoPricingAndAvailabilitiesSubFormData
        )) && gameInfoPricingAndAvailabilitiesSubFormData.isStarted;
      const isGameInfoTechnicalCapabilitiesFormValid =
        (await isIdXboxGameInformationTechnicalCapabilitiesFormValid(
          gameInfoTechnicalCapabilitiesSubFormData
        )) && gameInfoTechnicalCapabilitiesSubFormData.isStarted;
      const isGameInfoConfigurationsFormValid =
        (await isIdXboxGameInformationConfigurationsFormValid(gameInfoConfigurationsSubFormData)) &&
        gameInfoConfigurationsSubFormData.isStarted;
      const isGameInfoXboxNetworkFormValid =
        (await isIdXboxGameInformationXboxNetworkFormValid(gameInfoXboxNetworkSubFormData)) &&
        gameInfoXboxNetworkSubFormData.isStarted;
      const isGameInfoUgcFormValid =
        (await isIdXboxGameInformationUgcFormValid(gameInfoUgcSubFormData)) &&
        gameInfoUgcSubFormData.isStarted;
      const isGameInfoSupplementaryDataFormValid =
        (await isIdXboxGameInformationSupplementaryDataFormValid(
          gameInfoSupplementaryDataSubFormData
        )) && gameInfoSupplementaryDataSubFormData.isStarted;
      tempPercentage = increasePercentageIfValid(isGameInfoConceptFormValid, tempPercentage, 2);
      tempPercentage = increasePercentageIfValid(
        isGameInfoGameExpectationsFormValid,
        tempPercentage,
        2
      );
      tempPercentage = increasePercentageIfValid(isGameInfoPricingFormValid, tempPercentage);
      tempPercentage = increasePercentageIfValid(
        isGameInfoTechnicalCapabilitiesFormValid,
        tempPercentage
      );
      tempPercentage = increasePercentageIfValid(isGameInfoConfigurationsFormValid, tempPercentage);
      tempPercentage = increasePercentageIfValid(isGameInfoXboxNetworkFormValid, tempPercentage);
      tempPercentage = increasePercentageIfValid(isGameInfoUgcFormValid, tempPercentage);
      tempPercentage = increasePercentageIfValid(
        isGameInfoSupplementaryDataFormValid,
        tempPercentage
      );
      setPercentage(tempPercentage);
    };

    calculatePercentage();
  }, [
    currentStep,
    gameInfoConceptSubFormData,
    gameInfoGameExpectationsSubFormData,
    gameInfoConfigurationsSubFormData,
    gameInfoPricingAndAvailabilitiesSubFormData,
    gameInfoTechnicalCapabilitiesSubFormData,
    gameInfoXboxNetworkSubFormData,
    gameInfoUgcSubFormData,
    gameInfoSupplementaryDataSubFormData,
  ]);

  return percentage;
};

// percentage bar for ID@Xbox Welcome Page
export const useIdXboxWelcomePercentage = () => {
  const [percentage, setPercentage] = useState(0);
  const currentStep = useSelector(getIdXboxApplicationStep) ?? IdXboxApplicationStep.Unset;

  // ID@Xbox Introduction sub form data
  const idXboxIntroContactSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, IdXboxIntroFormId, IdXboxIntroSubFormsId.ContactInformation)
  ) as IdXboxIntroContactDataType;
  const idXboxIntroStudioSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, IdXboxIntroFormId, IdXboxIntroSubFormsId.StudioInformation)
  ) as IdXboxIntroStudioDataType;

  useEffect(() => {
    const calculatePercentage = async () => {
      if (currentStep === IdXboxApplicationStep.Unset) {
        setPercentage(0);
      }

      let tempPercentage = stepPercentageValue;

      // ID@Xbox Intro
      const isIdXboxContactFormValid =
        (await isIdXboxIntroContactFormValid(idXboxIntroContactSubFormData)) &&
        idXboxIntroContactSubFormData.isStarted;
      const isIdXboxStudioFormValid =
        (await isIdXboxIntroStudioFormValid(idXboxIntroStudioSubFormData)) &&
        idXboxIntroStudioSubFormData.isStarted;

      if (isIdXboxContactFormValid && isIdXboxStudioFormValid) {
        tempPercentage = validFormsPercentage;
      } else {
        tempPercentage = increasePercentageIfValid(isIdXboxContactFormValid, tempPercentage);
        tempPercentage = increasePercentageIfValid(isIdXboxStudioFormValid, tempPercentage);
      }
      const isNdaSent =
        compareIdXboxApplicationStepStatus(currentStep, IdXboxApplicationStep.NdaSent) >= 0;
      if (isNdaSent) {
        tempPercentage = increasePercentageIfValid(isNdaSent, tempPercentage);
      }
      const isNdaSigned =
        compareIdXboxApplicationStepStatus(currentStep, IdXboxApplicationStep.GameInformation) >= 0;
      if (isNdaSigned) {
        tempPercentage = increasePercentageIfValid(isNdaSigned, tempPercentage);
      }
      const isApplicationComplete =
        compareIdXboxApplicationStepStatus(
          currentStep,
          IdXboxApplicationStep.ApplicationComplete
        ) >= 0;
      if (isApplicationComplete) {
        setPercentage(100);
        return;
      }
      setPercentage(tempPercentage);
    };

    calculatePercentage();
  }, [currentStep, idXboxIntroContactSubFormData, idXboxIntroStudioSubFormData]);

  return percentage;
};
